import type { AxiosError } from 'axios'

import { parseAddEditOtherConsumption } from '../helpers/parseAddEditOtherConsumption'
import { initialPagination } from '@components/shared/Table/components/Pagination/types'
import { parseWeeklyBreakdownPayload } from '../helpers/parseWeeklyBreakdownPayload'
import { toast } from '@utils/functions/notificationToast'
import { useConsumption } from '.'

import type { WeeklyBreakdownFormDataProps } from './types'
import type { AddEditOtherConsumptionsModalFormProps } from '../components/modals/AddEditOtherConsumptions/form/types'
import type {
  ApprovalWorkflowConsumptionRequestProps,
  EditDetailedSummaryRequestProps,
  GetBigNumbersConsumptionRequestProps,
  GetConsumptionAveragePercentagesRequestProps,
  GetClassifiedConsumptionRequestProps,
  GetDetailedSummaryRequestProps,
  GetDetailedSummaryV2RequestProps,
  GetOtherConsumptionsRequestProps,
  ReleasePreviewConsumptionRequestProps,
  RemoveOtherConsumptionRequestProps
} from '@services/api/consumption/types'

import * as apiConsumption from '@services/api/consumption'

const reversePreviewConsumption = async (payload: ReleasePreviewConsumptionRequestProps) => {
  const { setIsReversePreviewConsumptionLoading } = useConsumption.getState()

  setIsReversePreviewConsumptionLoading(true)

  try {
    await apiConsumption.reversePreviewConsumption(payload)

    toast.success('Prévia revertida com sucesso!')

    getBigNumbersConsumption({
      filiCode: payload.filiCode,
      competenceDate: payload.competenceDate
    })

    return true
  } catch (error) {
    toast.error('Não foi possível reverter a prévia. Tente novamente', (error as AxiosError)?.status)

    return false
  } finally {
    setIsReversePreviewConsumptionLoading(false)
  }
}

const releasePreviewConsumption = async (payload: ReleasePreviewConsumptionRequestProps) => {
  const { setIsReleasePreviewConsumptionLoading } = useConsumption.getState()

  setIsReleasePreviewConsumptionLoading(true)

  try {
    await apiConsumption.releasePreviewConsumption(payload)

    toast.success('Prévia liberada com sucesso!')

    getBigNumbersConsumption({
      filiCode: payload.filiCode,
      competenceDate: payload.competenceDate
    })

    return true
  } catch (error) {
    toast.error('Não foi possível liberar a prévia. Tente novamente', (error as AxiosError)?.status)

    return false
  } finally {
    setIsReleasePreviewConsumptionLoading(false)
  }
}

const reverseWorkflowConsumption = async (payload: ApprovalWorkflowConsumptionRequestProps) => {
  const { setIsReverseWorkflowConsumptionLoading } = useConsumption.getState()

  setIsReverseWorkflowConsumptionLoading(true)

  try {
    await apiConsumption.reverseWorkflowConsumption(payload)

    toast.success('Prévia restaurada com sucesso!')

    getBigNumbersConsumption({
      filiCode: payload.filiCode,
      competenceDate: payload.competenceDate
    })

    return true
  } catch (error) {
    const errorMessage =
      (error as AxiosError<{ errorMessage: string[] }>)?.response?.data?.errorMessage[0] ||
      'Falha ao restaurar a prévia!'

    toast.error(errorMessage, (error as AxiosError)?.status)

    return false
  } finally {
    setIsReverseWorkflowConsumptionLoading(false)
  }
}

const approvalWorkflowConsumption = async (payload: ApprovalWorkflowConsumptionRequestProps) => {
  const { setIsApprovalWorkflowConsumptionLoading } = useConsumption.getState()

  setIsApprovalWorkflowConsumptionLoading(true)

  try {
    await apiConsumption.approvalWorkflowConsumption(payload)

    toast.success('Prévia enviada com sucesso!')

    getBigNumbersConsumption({
      filiCode: payload.filiCode,
      competenceDate: payload.competenceDate
    })

    return true
  } catch (error) {
    toast.error('Falha ao enviar a prévia!', (error as AxiosError)?.status)

    return false
  } finally {
    setIsApprovalWorkflowConsumptionLoading(false)
  }
}

const getBigNumbersConsumption = async (payload: GetBigNumbersConsumptionRequestProps) => {
  const { setIsBigNumbersConsumptionLoading, setBigNumbersConsumptionData } = useConsumption.getState()

  setIsBigNumbersConsumptionLoading(true)

  try {
    const data = await apiConsumption.getBigNumbersConsumption(payload)

    setBigNumbersConsumptionData(data)
  } catch (error) {
    toast.error('Falha ao buscar os números gerais de consumo!', (error as AxiosError)?.status)
  } finally {
    setIsBigNumbersConsumptionLoading(false)
  }
}

const getConsumptionAveragePercentages = async (payload: GetConsumptionAveragePercentagesRequestProps) => {
  const { setConsumptionAveragePercentagesData, setIsConsumptionAveragePercentagesLoading } = useConsumption.getState()

  setIsConsumptionAveragePercentagesLoading(true)

  try {
    const data = await apiConsumption.getConsumptionAveragePercentages(payload)

    setConsumptionAveragePercentagesData(data)
  } catch (error) {
    toast.error('Falha ao buscar as médias de consumo!', (error as AxiosError)?.status)
  } finally {
    setIsConsumptionAveragePercentagesLoading(false)
  }
}

const getWeeklyBreakdown = async (formData: WeeklyBreakdownFormDataProps) => {
  const { setIsWeeklyBreakdownLoading, setWeeklyBreakdownData } = useConsumption.getState()

  setIsWeeklyBreakdownLoading(true)

  try {
    const payload = parseWeeklyBreakdownPayload(formData)

    const data = await apiConsumption.getWeeklyBreakdown(payload)

    setWeeklyBreakdownData(data)
  } catch (error) {
    toast.error('Falha ao buscar o consumo agrupado!', (error as AxiosError)?.status)
  } finally {
    setIsWeeklyBreakdownLoading(false)
  }
}

const editDetailedSummary = async (payload: EditDetailedSummaryRequestProps) => {
  const { setIsEditDetailedSummaryLoading } = useConsumption.getState()

  setIsEditDetailedSummaryLoading(true)

  try {
    await apiConsumption.editDetailedSummary(payload)

    toast.success('O resumo foi atualizado com sucesso!')

    getDetailedSummary({
      filiCode: payload.branchCode,
      competenceDate: payload.competenceDate
    })

    getDetailedSummaryV2({
      filiCode: payload.branchCode,
      competenceDate: payload.competenceDate
    })

    getBigNumbersConsumption({
      filiCode: payload.branchCode,
      competenceDate: payload.competenceDate
    })

    return true
  } catch (error) {
    toast.error('Falha ao atualizar o resumo detalhado!', (error as AxiosError)?.status)

    return false
  } finally {
    setIsEditDetailedSummaryLoading(false)
  }
}

const getDetailedSummary = async (payload: GetDetailedSummaryRequestProps) => {
  const { setDetailedSummaryData, setIsDetailedSummaryLoading } = useConsumption.getState()

  setIsDetailedSummaryLoading(true)

  try {
    const data = await apiConsumption.getDetailedSummary(payload)

    setDetailedSummaryData(data)
  } catch (error) {
    toast.error('Falha ao buscar o resumo detalhado!', (error as AxiosError)?.status)
  } finally {
    setIsDetailedSummaryLoading(false)
  }
}

const getDetailedSummaryV2 = async (payload: GetDetailedSummaryV2RequestProps) => {
  const { setDetailedSummaryV2Data, setIsDetailedSummaryV2Loading } = useConsumption.getState()

  setIsDetailedSummaryV2Loading(true)

  try {
    const data = await apiConsumption.getDetailedSummaryV2(payload)

    setDetailedSummaryV2Data(data)
  } catch (error) {
    toast.error('Falha ao buscar o resumo detalhado!', (error as AxiosError)?.status)
  } finally {
    setIsDetailedSummaryV2Loading(false)
  }
}

const getClassifiedConsumption = async (payload: GetClassifiedConsumptionRequestProps) => {
  const { setClassifiedConsumptionData, setIsClassifiedConsumptionLoading } = useConsumption.getState()

  setIsClassifiedConsumptionLoading(true)

  try {
    const data = await apiConsumption.getClassifiedConsumption(payload)

    setClassifiedConsumptionData(data)
  } catch (error) {
    toast.error('Falha ao buscar o consumo classificado!', (error as AxiosError)?.status)
  } finally {
    setIsClassifiedConsumptionLoading(false)
  }
}

const removeOtherConsumption = async (payload: RemoveOtherConsumptionRequestProps) => {
  const { setIsRemoveOtherConsumptionLoading } = useConsumption.getState()

  setIsRemoveOtherConsumptionLoading(true)

  try {
    await apiConsumption.removeOtherConsumption(payload)

    toast.success('O outro consumo foi excluído com sucesso!')

    getOtherConsumptions({
      filiCode: payload.filiCode!,
      competenceDate: payload.competenceDate!,
      page: initialPagination.PAGE,
      itemsPerPage: initialPagination.ITEMS_PER_PAGE
    })

    getClassifiedConsumption({
      filiCode: payload.filiCode!,
      competenceDate: payload.competenceDate!
    })

    getDetailedSummary({
      filiCode: payload.filiCode!,
      competenceDate: payload.competenceDate!
    })

    getBigNumbersConsumption({
      filiCode: payload.filiCode!,
      competenceDate: payload.competenceDate!
    })

    return true
  } catch (error) {
    const errorMessage =
      (error as AxiosError<{ errorMessage: string[] }>)?.response?.data?.errorMessage[0] ||
      'Falha ao excluir o outro consumo!'

    toast.error(errorMessage, (error as AxiosError)?.status)

    return false
  } finally {
    setIsRemoveOtherConsumptionLoading(false)
  }
}

const editOtherConsumption = async (formData: AddEditOtherConsumptionsModalFormProps) => {
  const { setCreateNewOtherConsumptionLoading } = useConsumption.getState()

  setCreateNewOtherConsumptionLoading(true)

  try {
    const payload = parseAddEditOtherConsumption(formData)

    await apiConsumption.editOtherConsumption(payload)

    toast.success('O outro consumo foi editado com sucesso!')

    getOtherConsumptions({
      filiCode: formData.filiCode!,
      competenceDate: formData.competenceDate!,
      page: initialPagination.PAGE,
      itemsPerPage: initialPagination.ITEMS_PER_PAGE
    })

    getClassifiedConsumption({
      filiCode: formData.filiCode!,
      competenceDate: formData.competenceDate!
    })

    getDetailedSummary({
      filiCode: formData.filiCode!,
      competenceDate: formData.competenceDate!
    })

    getBigNumbersConsumption({
      filiCode: formData.filiCode!,
      competenceDate: formData.competenceDate!
    })

    return true
  } catch (error) {
    const errorMessage = (error as AxiosError)?.response?.status === 400
      ? (error as AxiosError<{ errorMessage: string[] }>)?.response?.data?.errorMessage[0] || 'Falha ao editar o outro consumo!'
      : 'Falha ao editar o outro consumo!'

    toast.error(errorMessage, (error as AxiosError)?.status)

    return false
  } finally {
    setCreateNewOtherConsumptionLoading(false)
  }
}

const createNewOtherConsumption = async (formData: AddEditOtherConsumptionsModalFormProps) => {
  const { setCreateNewOtherConsumptionLoading } = useConsumption.getState()

  setCreateNewOtherConsumptionLoading(true)

  try {
    const payload = parseAddEditOtherConsumption(formData)

    await apiConsumption.createNewOtherConsumption(payload)

    toast.success('O outro consumo foi adicionado com sucesso!')

    getOtherConsumptions({
      filiCode: formData.filiCode!,
      competenceDate: formData.competenceDate!,
      page: initialPagination.PAGE,
      itemsPerPage: initialPagination.ITEMS_PER_PAGE
    })

    getClassifiedConsumption({
      filiCode: formData.filiCode!,
      competenceDate: formData.competenceDate!
    })

    getDetailedSummary({
      filiCode: formData.filiCode!,
      competenceDate: formData.competenceDate!
    })

    getBigNumbersConsumption({
      filiCode: formData.filiCode!,
      competenceDate: formData.competenceDate!
    })

    return true
  } catch (error) {
    const errorMessage = (error as AxiosError)?.response?.status === 400
      ? (error as AxiosError<{ errorMessage: string[] }>)?.response?.data?.errorMessage[0] || 'Falha ao adicionar um novo outro consumo!'
      : 'Falha ao adicionar um novo outro consumo!'

    toast.error(errorMessage, (error as AxiosError)?.status)

    return false
  } finally {
    setCreateNewOtherConsumptionLoading(false)
  }
}

const getOtherConsumptions = async (payload: GetOtherConsumptionsRequestProps) => {
  const { setOtherConsumptionsData, setOtherConsumptionsLoading } = useConsumption.getState()

  setOtherConsumptionsLoading(true)

  try {
    const data = await apiConsumption.getOtherConsumptions(payload)

    setOtherConsumptionsData(data)
  } catch (error) {
    toast.error('Falha ao buscar os tipos de consumo!', (error as AxiosError)?.status)
  } finally {
    setOtherConsumptionsLoading(false)
  }
}

const getConsumptionTypes = async () => {
  const { setConsumptionTypes, setConsumptionTypesLoading } = useConsumption.getState()

  setConsumptionTypesLoading(true)

  try {
    const data = await apiConsumption.getConsumptionTypes()

    setConsumptionTypes(data)
  } catch (error) {
    toast.error('Falha ao buscar os tipos de consumo!', (error as AxiosError)?.status)
  } finally {
    setConsumptionTypesLoading(false)
  }
}

export {
  getConsumptionTypes,
  createNewOtherConsumption,
  getOtherConsumptions,
  editOtherConsumption,
  removeOtherConsumption,
  getClassifiedConsumption,
  getDetailedSummary,
  getDetailedSummaryV2,
  getWeeklyBreakdown,
  editDetailedSummary,
  getBigNumbersConsumption,
  getConsumptionAveragePercentages,
  approvalWorkflowConsumption,
  reverseWorkflowConsumption,
  releasePreviewConsumption,
  reversePreviewConsumption
}
